/**
 * PesapalTerminalDialog
 * Shows the live status of a Pesapal Cloud POS "Push to Pay" card-terminal
 * transaction and polls the backend until the cardholder completes or declines
 * the payment on the physical PDQ terminal. Mirrors DpoTerminalDialog.
 */

import { useEffect, useRef, useState, useCallback } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../../components/ui/dialog";
import { Button } from "../../components/ui/button";
import { CreditCard, CheckCircle, XCircle, Loader2 } from "lucide-react";
import { api } from "@/store/useStore";
import { toast } from "sonner";

export function PesapalTerminalDialog({ open, invoice, channel = "card", formatCurrency, onClose, onPaid }) {
  const isMpesa = channel === "mpesa";
  const initialMsg = isMpesa ? "STK sent — customer enters M-Pesa PIN…" : "Present card on the terminal…";
  const [state, setState] = useState("polling"); // polling | paid | failed
  const [message, setMessage] = useState(initialMsg);
  const [busy, setBusy] = useState(false);
  const timer = useRef(null);
  const polling = useRef(false);

  const invoiceId = invoice?.id;

  const poll = useCallback(async () => {
    if (!invoiceId || polling.current) return;
    polling.current = true;
    try {
      const res = await api.get(`/invoices/${invoiceId}/pesapal-pos-status`);
      if (res?.status === "paid") {
        setState("paid");
        setMessage("Card payment approved.");
        setTimeout(() => onPaid?.(res.receipt_lines || []), 900);
      } else if (res?.status === "failed") {
        setState("failed");
        setMessage(res.message || "Card payment was declined.");
      } else if (res?.status === "none") {
        setMessage("No payment in progress.");
      } else {
        setMessage(res?.message || "Waiting for card…");
      }
    } catch {
      /* keep polling */
    } finally {
      polling.current = false;
    }
  }, [invoiceId, onPaid]);

  useEffect(() => {
    if (!open) return;
    setState("polling");
    setMessage(initialMsg);
    poll();
    timer.current = setInterval(() => {
      setState((st) => {
        if (st === "polling") poll();
        return st;
      });
    }, 3000);
    return () => clearInterval(timer.current);
  }, [open, poll]);

  useEffect(() => {
    if (state !== "polling" && timer.current) clearInterval(timer.current);
  }, [state]);

  const cancel = async () => {
    setBusy(true);
    try {
      await api.post(`/invoices/${invoiceId}/pesapal-pos-cancel`);
      toast.info("Card transaction cancelled");
    } catch {
      /* ignore */
    } finally {
      setBusy(false);
      onClose?.();
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) { if (state === "polling") cancel(); else onClose?.(); } }}>
      <DialogContent className="sm:max-w-md" data-testid="pesapal-terminal-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CreditCard className={`h-5 w-5 ${isMpesa ? "text-green-600" : "text-blue-600"}`} /> {isMpesa ? "Pesapal M-Pesa" : "Pesapal Card Terminal"}
          </DialogTitle>
          <DialogDescription>{isMpesa ? "Pesapal · M-Pesa STK push" : "Pesapal Push to Pay · Terminal payment"}</DialogDescription>
        </DialogHeader>

        <div className="flex flex-col items-center gap-3 py-6">
          {state === "polling" ? (
            <Loader2 className="h-12 w-12 animate-spin text-blue-600" />
          ) : state === "paid" ? (
            <CheckCircle className="h-14 w-14 text-green-600" />
          ) : (
            <XCircle className="h-14 w-14 text-red-600" />
          )}
          {invoice?.total != null && (
            <div className="text-2xl font-extrabold text-blue-600" data-testid="pesapal-pos-amount">
              {formatCurrency ? formatCurrency(invoice.total) : invoice.total}
            </div>
          )}
          <p className="text-sm text-muted-foreground text-center px-4" data-testid="pesapal-pos-message">{message}</p>
        </div>

        <div className="flex gap-2">
          {state === "polling" ? (
            <>
              <Button variant="outline" className="flex-1" onClick={poll} data-testid="pesapal-pos-check-now-btn">Check now</Button>
              <Button variant="destructive" className="flex-1" onClick={cancel} disabled={busy} data-testid="pesapal-pos-cancel-btn">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Cancel"}
              </Button>
            </>
          ) : state === "paid" ? (
            <Button className="w-full" onClick={() => onPaid?.()} data-testid="pesapal-pos-done-btn">Done</Button>
          ) : (
            <>
              <Button variant="outline" className="flex-1" onClick={poll} data-testid="pesapal-pos-retry-btn">Try again</Button>
              <Button className="flex-1" onClick={onClose} data-testid="pesapal-pos-close-btn">Close</Button>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
